import { useState } from "react";
import axios from "axios";
import { jsPDF } from "jspdf";
import { Reveal } from "./Reveal";
import { Star } from "./Logo";
import { useLang } from "@/i18n/LanguageContext";
import { TrendingDown, Globe, Percent, Download, Loader2, Smartphone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAIN_ICONS = [Globe, TrendingDown, Percent, Smartphone];

const clean = (t) =>
  String(t)
    .replace(/″/g, '"')
    .replace(/[„“”]/g, '"')
    .replace(/[’‘]/g, "'")
    .replace(/–/g, "-")
    .replace(/…/g, "...")
    .replace(/[✦✨→]/g, "");

const buildMagazine = (t, lang, aiArticles) => {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = 595.28;
  const H = 841.89;
  const M = 52;
  const maxW = W - M * 2;
  const c = t.b2bChallenges;
  let page = 1;
  let y = 64;

  const footer = () => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(138, 127, 121);
    doc.text("MIRA BUSINESS  ·  mira-mirror.ai/business", M, H - 30);
    doc.text(String(page), W - M, H - 30, { align: "right" });
  };
  const newPage = () => {
    doc.addPage();
    page++;
    doc.setFillColor(252, 250, 248);
    doc.rect(0, 0, W, H, "F");
    footer();
    y = 64;
  };
  const ensure = (h) => {
    if (y + h > H - 60) newPage();
  };
  const para = (txt, size = 9, color = [90, 82, 78], style = "normal", indent = 0, lh = 12) => {
    doc.setFont("helvetica", style);
    doc.setFontSize(size);
    doc.setTextColor(...color);
    const lines = doc.splitTextToSize(clean(txt), maxW - indent);
    lines.forEach((ln) => {
      ensure(lh);
      doc.text(ln, M + indent, y);
      y += lh;
    });
  };
  const chapterTitle = (kicker, title) => {
    ensure(120);
    y += 10;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(198, 156, 109);
    doc.text(clean(kicker).toUpperCase(), M, y);
    y += 18;
    doc.setFont("times", "bold");
    doc.setFontSize(17);
    doc.setTextColor(44, 39, 37);
    const lines = doc.splitTextToSize(clean(title), maxW);
    lines.forEach((ln) => {
      ensure(20);
      doc.text(ln, M, y);
      y += 20;
    });
    y += 6;
  };
  const subHeading = (txt) => {
    ensure(40);
    y += 8;
    doc.setFont("times", "bold");
    doc.setFontSize(12);
    doc.setTextColor(44, 39, 37);
    const lines = doc.splitTextToSize(clean(txt), maxW);
    lines.forEach((ln) => {
      ensure(15);
      doc.text(ln, M, y);
      y += 15;
    });
    y += 3;
  };
  const numberedItem = (i, title, text) => {
    ensure(44);
    doc.setFillColor(212, 175, 55);
    doc.circle(M + 6, y - 3, 6.5, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(255, 255, 255);
    doc.text(String(i), M + 6, y - 0.5, { align: "center" });
    doc.setFontSize(10);
    doc.setTextColor(44, 39, 37);
    doc.text(clean(title), M + 20, y);
    y += 13;
    para(text, 8.5, [90, 82, 78], "normal", 20, 10.5);
    y += 7;
  };
  const claimLine = (txt) => {
    ensure(40);
    y += 8;
    doc.setFont("times", "bolditalic");
    doc.setFontSize(11.5);
    doc.setTextColor(198, 156, 109);
    const lines = doc.splitTextToSize("«" + clean(txt) + "»", maxW - 60);
    lines.forEach((ln) => {
      ensure(15);
      doc.text(ln, W / 2, y, { align: "center" });
      y += 15;
    });
    y += 6;
  };

  // ── Cover ──
  doc.setFillColor(38, 33, 28);
  doc.rect(0, 0, W, H, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(212, 175, 55);
  doc.text("M I R A   B U S I N E S S", M, 110);
  doc.setDrawColor(212, 175, 55);
  doc.setLineWidth(1);
  doc.line(M, 124, M + 120, 124);
  doc.setFont("times", "bold");
  doc.setFontSize(34);
  doc.setTextColor(252, 250, 248);
  const covTitle = doc.splitTextToSize(clean(c.magazineTitle), maxW);
  doc.text(covTitle, M, 200);
  const q = Math.floor(new Date().getMonth() / 3) + 1;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(198, 156, 109);
  doc.text(`${clean(c.issueLabel)} Q${q} ${new Date().getFullYear()}`, M, 200 + covTitle.length * 38 + 10);
  doc.setFontSize(10);
  doc.setTextColor(200, 190, 180);
  const covSub = doc.splitTextToSize(clean(c.sub), maxW - 60);
  doc.text(covSub, M, 200 + covTitle.length * 38 + 44);
  let cy = 200 + covTitle.length * 38 + 44 + covSub.length * 13 + 40;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(212, 175, 55);
  doc.text(clean(c.tocLabel).toUpperCase(), M, cy);
  cy += 20;
  const toc = [t.b2bTeam.title, t.windowLooks.title, t.caseStudy.title, ...aiArticles.map((a) => a.title)];
  toc.slice(0, 9).forEach((item) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(230, 222, 212);
    doc.text("-  " + clean(item).slice(0, 80), M, cy);
    cy += 17;
  });
  doc.setFontSize(8);
  doc.setTextColor(138, 127, 121);
  doc.text("MYSYMP AG  ·  mira-mirror.ai/business", M, H - 46);

  // ── Chapter 1: Team ──
  newPage();
  chapterTitle(t.b2bTeam.kicker, t.b2bTeam.title);
  para(t.b2bTeam.sub, 9.5, [90, 82, 78], "normal", 0, 12.5);
  y += 10;
  t.b2bTeam.args.forEach((a, i) => numberedItem(i + 1, a.t, a.d));
  claimLine(t.b2bTeam.claim);

  // ── Chapter 2: Window Looks ──
  newPage();
  chapterTitle(t.windowLooks.kicker, t.windowLooks.title);
  para(t.windowLooks.sub, 9.5, [90, 82, 78], "normal", 0, 12.5);
  y += 10;
  t.windowLooks.steps.forEach((s, i) => numberedItem(i + 1, s.t, s.d));
  y += 4;
  t.windowLooks.facts.forEach((f) => {
    ensure(14);
    doc.setFillColor(212, 175, 55);
    doc.circle(M + 3, y - 2.5, 2, "F");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(90, 82, 78);
    doc.text(clean(f), M + 11, y);
    y += 13;
  });
  claimLine(t.windowLooks.claim);

  // ── Chapter 3: Case Study ──
  newPage();
  chapterTitle(t.caseStudy.kicker, t.caseStudy.title);
  para(t.caseStudy.intro, 9.5, [90, 82, 78], "normal", 0, 12.5);
  para(t.caseStudy.disclaimer, 7, [138, 127, 121], "oblique", 0, 9);
  y += 8;
  t.caseStudy.sections.forEach((s) => {
    subHeading(s.h);
    para(s.p, 8.5, [90, 82, 78], "normal", 0, 11);
    y += 4;
  });
  claimLine(t.caseStudy.quote);

  // ── AI B2B articles ──
  aiArticles.slice(0, 4).forEach((a) => {
    newPage();
    chapterTitle(c.fromMagazineLabel, a.title);
    if (a.excerpt) para(a.excerpt, 9.5, [90, 82, 78], "oblique", 0, 12.5);
    y += 8;
    (a.sections || []).forEach((s) => {
      subHeading(s.h);
      para(s.p, 8.5, [90, 82, 78], "normal", 0, 11);
      y += 4;
    });
  });

  // ── Back page CTA ──
  newPage();
  y = 300;
  doc.setFont("times", "bold");
  doc.setFontSize(20);
  doc.setTextColor(44, 39, 37);
  const cta = doc.splitTextToSize(clean(c.backTitle), maxW - 60);
  doc.text(cta, W / 2, y, { align: "center" });
  y += cta.length * 24 + 16;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(90, 82, 78);
  const bt = doc.splitTextToSize(clean(c.backText), maxW - 80);
  doc.text(bt, W / 2, y, { align: "center" });
  y += bt.length * 13 + 24;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(198, 156, 109);
  doc.text("mira-mirror.ai/business", W / 2, y, { align: "center" });
  y += 20;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(90, 82, 78);
  doc.text("info@mysymp.ch  ·  +41 79 471 06 25", W / 2, y, { align: "center" });

  doc.save(`mira-business-magazin-q${q}-${new Date().getFullYear()}.pdf`);
};

export default function B2BChallenges() {
  const { t, lang } = useLang();
  const c = t.b2bChallenges;
  const [busy, setBusy] = useState(false);
  if (!c) return null;

  const download = async () => {
    setBusy(true);
    try {
      let aiArticles = [];
      try {
        const r = await axios.get(`${API}/blog/articles?lang=${lang}`);
        const arts = Array.isArray(r.data) ? r.data : r.data.articles || [];
        aiArticles = arts.filter((a) => a.category === "B2B");
      } catch (e) {
        aiArticles = [];
      }
      buildMagazine(t, lang, aiArticles);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="py-20 sm:py-28" style={{ background: "#262119", color: "#faf6f0" }} data-testid="b2b-challenges-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="max-w-3xl">
          <p className="flex items-center gap-2 text-xs uppercase font-medium" style={{ color: "var(--gold)", letterSpacing: "0.16em" }}>
            <Star size={11} /> {c.kicker}
          </p>
          <h2 className="mt-5 font-serif-display text-4xl sm:text-5xl font-semibold leading-tight" data-testid="b2b-challenges-title">
            {c.title}
          </h2>
          <p className="mt-5 text-base md:text-lg leading-relaxed" style={{ color: "rgba(250,246,240,0.72)" }}>
            {c.sub}
          </p>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {c.pains.map((p, i) => {
            const Icon = PAIN_ICONS[i];
            return (
              <Reveal key={i} delay={i * 0.08} className="rounded-2xl border p-6" style={{ borderColor: "rgba(212,175,55,0.3)", background: "rgba(250,246,240,0.04)" }} data-testid={`b2b-pain-${i + 1}`}>
                <span className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: "rgba(212,175,55,0.14)" }}>
                  <Icon size={19} strokeWidth={1.7} style={{ color: "var(--gold)" }} />
                </span>
                <h3 className="mt-4 text-base font-semibold" style={{ color: "#faf6f0" }}>{p.t}</h3>
                {p.q && (
                  <p className="mt-3 border-l-2 pl-3 font-serif-display text-[15px] italic leading-snug" style={{ borderColor: "var(--gold)", color: "rgba(250,246,240,0.85)" }} data-testid={`b2b-pain-quote-${i + 1}`}>
                    «{p.q}»
                  </p>
                )}
                <p className="mt-2 text-sm leading-relaxed" style={{ color: "rgba(250,246,240,0.65)" }}>{p.d}</p>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.2} className="mt-12 flex flex-col items-start gap-6 rounded-2xl border p-7 sm:flex-row sm:items-center sm:justify-between" style={{ borderColor: "var(--gold)", background: "rgba(212,175,55,0.08)" }} data-testid="b2b-magazine-block">
          <div className="max-w-xl">
            <h3 className="font-serif-display text-xl sm:text-2xl font-semibold" style={{ color: "#faf6f0" }} data-testid="b2b-magazine-title">
              {c.magazineTitle}
            </h3>
            <p className="mt-2 text-sm leading-relaxed" style={{ color: "rgba(250,246,240,0.7)" }}>{c.magazineText}</p>
            <p className="mt-2 text-xs" style={{ color: "rgba(250,246,240,0.45)" }}>{c.magazineNote}</p>
          </div>
          <button
            onClick={download}
            disabled={busy}
            data-testid="b2b-magazine-download-btn"
            className="pill-btn inline-flex shrink-0 items-center gap-2 rounded-full px-7 py-4 text-sm font-semibold"
            style={{ background: "var(--gold)", color: "#262119" }}
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} {c.magazineCta}
          </button>
        </Reveal>
      </div>
    </section>
  );
}
